/**
 * Main component module for Blazor interop.
 * Re-exports all functionality from modularized submodules.
 * Maintains backward compatibility with existing API.
 */

'use strict';

// Type exports
export type {
    DotNetObjectReference,
    KeyboardPayload,
    BreakpointInfo,
    BreakpointName,
    NetworkInfo,
    SizeInfo,
    MaskPayload,
    MaskConfig,
    MaskKind,
    MaskValueMode,
    StorageKind,
    InstallPromptOutcome
} from './types.js';

// PWA functionality
import * as pwaModule from './modules/pwa.js';

export const pwaInit = pwaModule.initialize;
export const init = pwaModule.initialize;
export const pwaPromptInstall = pwaModule.promptInstall;
export const promptInstall = pwaModule.promptInstall;
export const pwaDispose = pwaModule.dispose;
export const dispose = pwaModule.dispose;

// Service Worker functionality
import * as serviceWorkerModule from './modules/serviceWorker.js';

export const swInitUpdateWatcher = serviceWorkerModule.initializeUpdateWatcher;
export const swDispose = serviceWorkerModule.dispose;
export const swReload = serviceWorkerModule.reload;

// Network functionality
import * as networkModule from './modules/network.js';

export const netInit = networkModule.initialize;
export const netDispose = networkModule.dispose;
export const netIsOnline = networkModule.isOnline;
export const netInfo = networkModule.getNetworkInfo;

// Keyboard functionality
import * as keyboardModule from './modules/keyboard.js';

export const kbInit = keyboardModule.initialize;
export const kbDispose = keyboardModule.dispose;

// Breakpoint functionality
import * as breakpointModule from './modules/breakpoint.js';

export const bpInit = breakpointModule.initialize;
export const bpGet = breakpointModule.getCurrent;
export const bpDispose = breakpointModule.dispose;

// ResizeObserver functionality
import * as resizeObserverModule from './modules/resizeObserver.js';

export const roObserve = resizeObserverModule.observe;
export const roUnobserve = resizeObserverModule.unobserve;

// Storage functionality
import * as storageModule from './modules/storage.js';

export const storageGet = storageModule.getItem;
export const storageSet = storageModule.setItem;
export const storageRemove = storageModule.removeItem;
export const storageClear = storageModule.clear;

// Focus utilities
import * as focusModule from './modules/focus.js';

export const focusById = focusModule.focusById;
export const focusSelector = focusModule.focusBySelector;
export const scrollIntoViewById = focusModule.scrollIntoViewById;

// Mask functionality
import * as maskModule from './modules/mask.js';

export const maskInit = maskModule.initialize;
export const maskDispose = maskModule.dispose;
export const maskSet = maskModule.setValue;
export const maskGet = maskModule.getValue;
export const maskUpdate = maskModule.updateOptions;

// DOM utilities
import * as domModule from './modules/dom.js';

export const getAttribute = domModule.getAttribute;
export const setAttribute = domModule.setAttribute;
export const removeAttribute = domModule.removeAttribute;
export const setIndeterminate = domModule.setIndeterminate;
export const prefersDarkMode = domModule.prefersDarkMode;
export const clipboardWriteText = domModule.writeClipboardText;
export const clipboardReadText = domModule.readClipboardText;

// Select functionality
import * as selectModule from './modules/select.js';

export const selectInitClickOutside = selectModule.initializeClickOutside;
export const selectDisposeClickOutside = selectModule.disposeClickOutside;
export const selectDisposeAll = selectModule.disposeAll;

// MenuSelect functionality
import * as menuModule from './modules/menu.js';

export const menuInit = menuModule.initializeMenu;
export const menuDispose = menuModule.disposeMenu;
export const menuDisposeAll = menuModule.disposeAll;
export const contextMenuInit = menuModule.initializeContextMenu;

// Form functionality
import * as formModule from './modules/form.js';

export const formInitEnterAsTab = formModule.initializeEnterAsTab;
export const formDisposeEnterAsTab = formModule.disposeEnterAsTab;
export const formFocusFirst = formModule.focusFirst;
export const formDisposeAll = formModule.disposeAll;

// DataGrid functionality
import * as gridModule from './modules/grid.js';

export const gridHighlight = gridModule.highlight;
export const gridCollectRowTexts = gridModule.collectRowTexts;

// HtmlEditor functionality
import * as htmlEditorModule from './modules/htmlEditor.js';

export const htmlEditorInit = htmlEditorModule.initialize;
export const htmlEditorDispose = htmlEditorModule.dispose;
export const htmlEditorExec = htmlEditorModule.exec;
export const htmlEditorGetHtml = htmlEditorModule.getHtml;
export const htmlEditorSetHtml = htmlEditorModule.setHtml;
export const htmlEditorFocus = htmlEditorModule.focus;
